// Concentration-unit normalization for the scenario analysis counterfactual.
//
// NOT unitConversion.js. That module formats concentrations for DISPLAY and
// returns an unconvertible pair unchanged. This one prepares values for
// ARITHMETIC and throws on such a pair, because the impact query substitutes a
// hypothetical concentration into historical infusion data — a silently
// unconverted value there yields a plausible wrong container count via
// CEIL(units_infused / (conc * volume)) rather than an error.
//
// When the impact analysis moves behind a Looker-backed endpoint, the server
// must mirror this logic exactly.

/**
 * Scale of each supported numerator unit relative to grams.
 *
 * "units" (insulin, heparin, etc.) is an activity unit with no gram equivalent.
 * It is present with scale 1 so a units -> units conversion resolves cleanly
 * (factor 1) instead of falling through to a missing-entry throw, which would
 * abort the whole impact calculation for that medication.
 */
export const UNIT_SCALE = {
  mcg: 1e-6,
  mg: 1e-3,
  g: 1,
  units: 1,
};

/**
 * Units that are NOT interconvertible with the gram scale.
 *
 * `units` shares the numeric scale of `g` (both 1) purely so a units -> units
 * conversion yields factor 1. That coincidence means a naive scale-ratio
 * conversion would happily turn 2 U/mL into "2000 mg/mL" — a dimension error
 * dressed up as a plausible number. Anything listed here may only convert to
 * itself; a cross-dimension pair throws instead.
 */
const ACTIVITY_UNITS = new Set(['units']);

/**
 * Extract the canonical numerator unit from a concentration unit.
 *
 * Handles "mg/mL", "mcg/ml", "µg/mL", "U/mL", "units/mL". Two normalizations
 * are applied, and their ORDER MATTERS:
 *
 *   1. micro sign / Greek mu -> "mcg"  (must run first, so "µg" is not mangled
 *      into "u" by the activity-unit rule below)
 *   2. "u" / "units" -> "units"        (canonicalizes the activity unit so equal
 *      units string-match rather than hitting the gram-based scale table)
 *
 * @param {string} unit - e.g. "mcg/mL"
 * @returns {string} the canonical numerator, e.g. "mcg"
 * @throws {Error} when the unit does not look like "<numerator>/<denominator>"
 */
export function getNumeratorUnit(unit) {
  const match = String(unit ?? '')
    .trim()
    .match(/([a-zµμ]+)\s*\/\s*[a-z]+/i);
  if (!match) {
    throw new Error(`Unrecognized concentration unit: ${unit}`);
  }
  let numerator = match[1].toLowerCase();

  // Normalize micro sign / Greek mu on microgram -> mcg. Do this BEFORE the
  // activity-unit normalization so "µg" doesn't get mangled into "u"/"units".
  numerator = numerator.replace('µg', 'mcg').replace('μg', 'mcg');

  // Insulin (and similar drugs) is dosed in "units", which shows up as "U",
  // "u", or spelled out. Map them all to "units" so equal units string-match
  // instead of falling through to the gram-based scale table.
  if (numerator === 'u' || numerator === 'units') {
    numerator = 'units';
  }

  return numerator;
}

/**
 * Convert `updatedConcentration` into the same units as
 * `currentConcentrationUnit`.
 *
 * e.g. 320 mcg/mL expressed in mg/mL -> 0.32
 *
 * @param {number} currentConcentration - unused, kept for signature parity with
 *   the original implementation (the conversion depends only on the units)
 * @param {string} currentConcentrationUnit - target unit, e.g. "mg/mL"
 * @param {number} updatedConcentration - value to convert
 * @param {string} updatedConcentrationUnit - source unit, e.g. "mcg/mL"
 * @returns {number} updatedConcentration expressed in currentConcentrationUnit
 * @throws {Error} on an unsupported unit pair (e.g. units -> mg)
 */
export function normalizeUpdatedConcentration(
  currentConcentration,
  currentConcentrationUnit,
  updatedConcentration,
  updatedConcentrationUnit
) {
  const currentNumerator = getNumeratorUnit(currentConcentrationUnit);
  const updatedNumerator = getNumeratorUnit(updatedConcentrationUnit);

  // If numerators already match (e.g. both mg/mL), no change needed.
  if (currentNumerator === updatedNumerator) {
    return updatedConcentration;
  }

  // Reject cross-dimension conversions before touching the scale table. Reached
  // only when the numerators differ, so an activity unit on either side now
  // means the pair is mixed (e.g. units -> mg) and has no meaningful factor.
  if (ACTIVITY_UNITS.has(currentNumerator) || ACTIVITY_UNITS.has(updatedNumerator)) {
    throw new Error(`Unsupported unit conversion: ${updatedNumerator} -> ${currentNumerator}`);
  }

  const currentScale = UNIT_SCALE[currentNumerator];
  const updatedScale = UNIT_SCALE[updatedNumerator];

  if (currentScale == null || updatedScale == null) {
    throw new Error(`Unsupported unit conversion: ${updatedNumerator} -> ${currentNumerator}`);
  }

  // Convert: updatedValue * (updatedScale / currentScale)
  // e.g. 320 mcg/mL -> mg/mL: 1e-6 / 1e-3 = 0.001 -> 0.32
  return updatedConcentration * (updatedScale / currentScale);
}

/**
 * Normalize a concentration to micrograms, for equality comparison only.
 *
 * Deliberately lenient: anything that isn't recognizably milligrams is treated
 * as already-mcg. This backs the ASHP / RTU alignment badges, which compare a
 * formulation string against a catalog entry and must not throw on an exotic
 * unit.
 *
 * @param {number} value
 * @param {string} unit - e.g. "mg/mL", "mcg/mL", "mg"
 * @returns {number} the value in mcg
 */
export function normalizeToMcg(value, unit) {
  const lowerUnit = String(unit ?? '').toLowerCase();
  if (lowerUnit.includes('mg') && !lowerUnit.includes('mcg')) {
    return value * 1000;
  }
  return value;
}
